import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Client side self heal for `profiles.active_certification_id`.
 *
 * A stored active certification can go stale between server side cleanups,
 * for example when a Stripe purchase expires. If the learner still holds a
 * valid certification we switch to it, and we write the correction back to
 * the profile so it sticks beyond this render.
 *
 * Returns the certification id that should be treated as active right now.
 */
export function useActiveCertificationSelfHeal(
  accessibleIds: string[],
  isLoaded: boolean,
  isStale = false,
): { effectiveActiveCertId: string | null; isHealing: boolean } {
  const { user, profile, refreshProfile, isAdmin, isSuperAdmin } = useAuth();
  const stored = profile?.active_certification_id ?? null;
  const [isHealing, setIsHealing] = useState(false);
  const attemptedRef = useRef<string | null>(null);

  const storedIsValid = !!stored && accessibleIds.includes(stored);
  const fallback = accessibleIds[0] ?? null;
  // Only switch to a real replacement. Never write null from the client:
  // that is the server side `reconcile_active_certifications()` job's work,
  // and a failed access refresh must not look like "no certifications".
  const needsHeal =
    !!user &&
    isLoaded &&
    !isStale &&
    !isAdmin &&
    !isSuperAdmin &&
    !!stored &&
    !storedIsValid &&
    !!fallback;


  useEffect(() => {
    if (!needsHeal || !user) return;
    // Only try once per stale value, so a failing write cannot loop.
    const key = `${user.id}:${stored}:${fallback ?? "none"}`;
    if (attemptedRef.current === key) return;
    attemptedRef.current = key;

    let cancelled = false;
    (async () => {
      setIsHealing(true);
      const { error } = await supabase
        .from("profiles")
        .update({ active_certification_id: fallback })
        .eq("id", user.id);
      if (error) console.error("active certification self heal failed", error);
      else await refreshProfile();
      if (!cancelled) setIsHealing(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [needsHeal, user, stored, fallback, refreshProfile]);

  return {
    effectiveActiveCertId: storedIsValid ? stored : fallback,
    isHealing,
  };
}
